import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Navigation, User, Car, Phone, Mail, IndianRupee, Fuel, Route, FileText, Edit2, Save, X, CreditCard, CheckCircle, AlertCircle, Banknote } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Toast from '../../components/ui/Toast';
import JourneyInvoice from '../../components/Invoice/JourneyInvoice';
import { formatDateTime, formatCurrency, formatName, amountInWords } from '../../lib/utils';
import { journeyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const JourneyDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { travelsInfo, user } = useAuth();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  // Edit costs state
  const [showEditCosts, setShowEditCosts] = useState(false);
  const [editingCosts, setEditingCosts] = useState({
    tollCharges: '',
    parkingCharges: '',
    otherCharges: '',
    otherChargesNote: '',
    discount: '',
    discountNote: ''
  });
  const [saving, setSaving] = useState(false);

  // Payment status state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paymentStatus: '',
    paidAmount: '',
    paymentMethod: '',
    paymentNote: ''
  });
  const [paymentSaving, setPaymentSaving] = useState(false);

  // Edit details state (distance, duration, fuel, odometer)
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [editingDetails, setEditingDetails] = useState({
    actualDistance: '',
    actualDuration: '',
    fuelConsumed: '',
    startOdometer: '',
    endOdometer: ''
  });
  const [detailsSaving, setDetailsSaving] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isDriver = user?.role === 'driver';

  useEffect(() => {
    fetchJourneyDetails();
  }, [id]);

  const fetchJourneyDetails = async () => {
    try {
      setLoading(true);
      const response = await journeyAPI.getJourneyById(id);
      setJourney(response.data.journey);
    } catch (error) {
      console.error('Error fetching journey details:', error);
      setError('Failed to load journey details');
    } finally {
      setLoading(false);
    }
  };

  // Open edit costs modal
  const handleEditCosts = () => {
    // Prefer the stored base fare (computed correctly per rental type at trip
    // completion). Only fall back to a km estimate if no base cost was recorded.
    const kmEstimate = (journey.actualDistance || 0) * (journey.carId?.pricePerKm || 0);

    setEditingCosts({
      baseCost: journey.baseCost || kmEstimate || '',
      tollCharges: journey.tollCharges || '',
      parkingCharges: journey.parkingCharges || '',
      otherCharges: journey.otherCharges || '',
      otherChargesNote: journey.otherChargesNote || '',
      discount: journey.discount || '',
      discountNote: journey.discountNote || ''
    });
    setShowEditCosts(true);
  };

  // Save updated costs
  const handleSaveCosts = async () => {
    setSaving(true);
    try {
      const response = await journeyAPI.updateCosts(journey._id, {
        baseCost: parseFloat(editingCosts.baseCost) || 0,
        tollCharges: parseFloat(editingCosts.tollCharges) || 0,
        parkingCharges: parseFloat(editingCosts.parkingCharges) || 0,
        otherCharges: parseFloat(editingCosts.otherCharges) || 0,
        otherChargesNote: editingCosts.otherChargesNote,
        discount: parseFloat(editingCosts.discount) || 0,
        discountNote: editingCosts.discountNote
      });

      if (response.data.success) {
        setJourney(response.data.journey);
        setShowEditCosts(false);
      }
    } catch (err) {
      console.error('Error updating costs:', err);
      setToast({ isOpen: true, title: 'Error', message: err.response?.data?.message || 'Failed to update costs', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Open payment status modal
  const handleOpenPayment = () => {
    setPaymentForm({
      paymentStatus: journey.paymentStatus || 'pending',
      paidAmount: journey.paidAmount || journey.totalCost || '',
      paymentMethod: journey.paymentMethod || 'cash',
      paymentNote: journey.paymentNote || ''
    });
    setShowPaymentModal(true);
  };

  // Save payment status
  const handleSavePayment = async () => {
    setPaymentSaving(true);
    try {
      const response = await journeyAPI.updatePaymentStatus(journey._id, {
        paymentStatus: paymentForm.paymentStatus,
        paidAmount: parseFloat(paymentForm.paidAmount) || 0,
        paymentMethod: paymentForm.paymentMethod,
        paymentNote: paymentForm.paymentNote
      });

      if (response.data.success) {
        setJourney(response.data.journey);
        setShowPaymentModal(false);
      }
    } catch (err) {
      console.error('Error updating payment:', err);
      setToast({ isOpen: true, title: 'Error', message: err.response?.data?.message || 'Failed to update payment status', type: 'error' });
    } finally {
      setPaymentSaving(false);
    }
  };

  // Open edit details modal
  const handleEditDetails = () => {
    setEditingDetails({
      actualDistance: journey.actualDistance || '',
      actualDuration: journey.actualDuration || '',
      fuelConsumed: journey.fuelConsumed || '',
      startOdometer: journey.startOdometer || '',
      endOdometer: journey.endOdometer || ''
    });
    setShowEditDetails(true);
  };

  // Save updated details
  const handleSaveDetails = async () => {
    setDetailsSaving(true);
    try {
      const response = await journeyAPI.updateDetails(journey._id, {
        actualDistance: parseFloat(editingDetails.actualDistance) || 0,
        actualDuration: parseFloat(editingDetails.actualDuration) || 0,
        fuelConsumed: parseFloat(editingDetails.fuelConsumed) || 0,
        startOdometer: parseFloat(editingDetails.startOdometer) || 0,
        endOdometer: parseFloat(editingDetails.endOdometer) || 0
      });

      if (response.data.success) {
        setJourney(response.data.journey);
        setShowEditDetails(false);
      }
    } catch (err) {
      console.error('Error updating details:', err);
      setToast({ isOpen: true, title: 'Error', message: err.response?.data?.message || 'Failed to update details', type: 'error' });
    } finally {
      setDetailsSaving(false);
    }
  };

  const getPaymentBadge = (status) => {
    const config = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Payment Pending' },
      'partial': { color: 'bg-orange-100 text-orange-800', icon: Banknote, label: 'Partially Paid' },
      'paid': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Paid' },
      'refunded': { color: 'bg-red-100 text-red-800', icon: CreditCard, label: 'Refunded' }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${c.color}`}>
        <Icon className="h-4 w-4" />
        {c.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'scheduled': { variant: 'warning', label: 'Scheduled' },
      'pending': { variant: 'warning', label: 'Pending' },
      'in-progress': { variant: 'success', label: 'In Progress' },
      'completed': { variant: 'secondary', label: 'Completed' },
      'cancelled': { variant: 'danger', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getProgressPercentage = () => {
    if (!journey) return 0;
    if (journey.actualDistance && journey.estimatedDistance) {
      return Math.min(100, Math.round((journey.actualDistance / journey.estimatedDistance) * 100));
    }
    if (journey.status === 'completed') return 100;
    if (journey.status === 'in-progress') return 50;
    return 0;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Journey Details"
        />
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-500">Loading journey details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !journey) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Journey Details"
          action={
            <Button variant="outline" onClick={() => navigate('/journeys')}>
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Journeys
            </Button>
          }
        />
        <Card>
          <CardContent className="text-center py-12">
            <Route className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{error || 'Journey not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={`Journey #${journey._id.slice(-6)}`}
        action={
          <div className="flex gap-2">
            {journey.status === 'completed' && (
              <Button size="sm" onClick={() => setShowInvoice(true)}>
                <FileText className="h-4 w-4 mr-1" />
                Invoice
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => navigate('/journeys')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Journey Route + Stats combined */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Route</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {(() => {
                const fromAddr = journey.fromLocation?.address || 'N/A';
                const toAddr = journey.toLocation?.address || 'N/A';
                const isRoundTrip = fromAddr.toLowerCase().trim() === toAddr.toLowerCase().trim();

                return (
                  <div className="space-y-3">
                    {/* Round Trip Badge */}
                    {isRoundTrip && (
                      <div className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full text-xs font-medium w-fit">
                        <Route className="h-3 w-3" />
                        Round Trip
                      </div>
                    )}

                    {/* From Location */}
                    <div className="flex items-start">
                      <div className="flex flex-col items-center mr-3">
                        <div className="w-3 h-3 rounded-full bg-green-600" />
                        {!isRoundTrip && <div className="w-0.5 h-10 bg-gray-300" />}
                        {isRoundTrip && <div className="w-0.5 h-6 bg-gray-300" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">{isRoundTrip ? 'Start & End Location' : 'Pickup'}</p>
                        <p className="font-medium text-sm">{fromAddr}</p>
                        {journey.fromLocation?.city && (
                          <p className="text-xs text-gray-500">{journey.fromLocation.city}, {journey.fromLocation.state}</p>
                        )}
                        <div className="flex gap-3 text-xs text-blue-600">
                          <span>Start: {formatDateTime(journey.startTime)}</span>
                          {isRoundTrip && journey.endTime && (
                            <span>End: {formatDateTime(journey.endTime)}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Waypoints */}
                    {journey.waypoints && journey.waypoints.length > 0 && (
                      journey.waypoints.map((waypoint, index) => (
                        <div key={index} className="flex items-start">
                          <div className="flex flex-col items-center mr-3">
                            <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                            <div className="w-0.5 h-8 bg-gray-300" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{waypoint.location}</p>
                            <p className="text-xs text-gray-500">
                              {waypoint.purpose}{waypoint.arrivalTime && ` · ${formatDateTime(waypoint.arrivalTime)}`}
                            </p>
                          </div>
                        </div>
                      ))
                    )}

                    {/* To Location — only show if NOT round trip */}
                    {!isRoundTrip && (
                      <div className="flex items-start">
                        <div className="flex flex-col items-center mr-3">
                          <div className="w-3 h-3 rounded-full bg-red-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Drop</p>
                          <p className="font-medium text-sm">{toAddr}</p>
                          {journey.toLocation?.city && (
                            <p className="text-xs text-gray-500">{journey.toLocation.city}, {journey.toLocation.state}</p>
                          )}
                          {journey.endTime && (
                            <p className="text-xs text-blue-600">{formatDateTime(journey.endTime)}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Stats row */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t mb-1">
                <span className="text-xs font-semibold text-gray-700">Trip Stats</span>
                {(isAdmin || isDriver) && (journey.status === 'completed' || journey.status === 'in-progress') && (
                  <button onClick={handleEditDetails} className="text-xs text-blue-600 hover:underline flex items-center">
                    <Edit2 className="h-3 w-3 mr-0.5" />
                    Edit
                  </button>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center p-2 bg-blue-50 rounded">
                  <p className="text-xs text-gray-500">Distance</p>
                  <p className="text-sm font-bold text-blue-600">
                    {journey.actualDistance || journey.estimatedDistance || 0} km
                  </p>
                </div>
                <div className="text-center p-2 bg-green-50 rounded">
                  <p className="text-xs text-gray-500">Duration</p>
                  <p className="text-sm font-bold text-green-600">
                    {journey.actualDuration?.toFixed(1) || journey.estimatedDuration || 0} hrs
                  </p>
                </div>
                <div className="text-center p-2 bg-yellow-50 rounded">
                  <p className="text-xs text-gray-500">Fuel</p>
                  <p className="text-sm font-bold text-yellow-600">
                    {journey.fuelConsumed || 0} L
                  </p>
                </div>
                <div className="text-center p-2 bg-purple-50 rounded">
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-sm font-bold text-purple-600">
                    {formatCurrency(journey.totalCost || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cost Breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center">
                  <IndianRupee className="h-4 w-4 mr-1" />
                  Cost Breakdown
                </CardTitle>
                {(isAdmin || isDriver) && (journey.status === 'completed' || journey.status === 'in-progress') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleEditCosts}
                    className="text-xs h-7 px-2"
                  >
                    <Edit2 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {(() => {
                const kmEstimate = (journey.actualDistance || 0) * (journey.carId?.pricePerKm || 0);
                const baseFare = journey.baseCost || kmEstimate || 0;
                const toll = journey.tollCharges || 0;
                const parking = journey.parkingCharges || 0;
                const other = journey.otherCharges || 0;
                const discount = journey.discount || 0;
                const subtotal = baseFare + toll + parking + other;
                const grandTotal = subtotal - discount;

                return (
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-xs">
                        Base ({journey.actualDistance || 0} km × {formatCurrency(journey.carId?.pricePerKm || 0)})
                      </span>
                      <span className="font-medium text-xs">{formatCurrency(baseFare)}</span>
                    </div>
                    {toll > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Toll</span>
                        <span className="font-medium">+ {formatCurrency(toll)}</span>
                      </div>
                    )}
                    {parking > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Parking</span>
                        <span className="font-medium">+ {formatCurrency(parking)}</span>
                      </div>
                    )}
                    {other > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Other {journey.otherChargesNote && `(${journey.otherChargesNote})`}</span>
                        <span className="font-medium">+ {formatCurrency(other)}</span>
                      </div>
                    )}
                    {journey.otherExpenses && journey.otherExpenses.length > 0 && (
                      journey.otherExpenses.map((expense, index) => (
                        <div key={index} className="flex justify-between text-xs">
                          <span className="text-gray-600">{expense.description}</span>
                          <span className="font-medium">+ {formatCurrency(expense.amount)}</span>
                        </div>
                      ))
                    )}
                    {discount > 0 && (
                      <div className="flex justify-between text-xs text-green-600">
                        <span>Discount {journey.discountNote && `(${journey.discountNote})`}</span>
                        <span className="font-medium">- {formatCurrency(discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-gray-300">
                      <span className="font-bold text-sm">Grand Total</span>
                      <span className="font-bold text-sm text-blue-600">{formatCurrency(grandTotal)}</span>
                    </div>
                    <p className="text-[11px] italic text-gray-500 text-right">{amountInWords(grandTotal)}</p>
                    {journey.totalCost && Math.abs(journey.totalCost - grandTotal) > 1 && (
                      <p className="text-xs text-yellow-700 bg-yellow-50 p-1.5 rounded mt-1">
                        Stored total ({formatCurrency(journey.totalCost)}) differs. Edit to update.
                      </p>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Notes */}
          {(journey.driverNotes || journey.customerFeedback) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Notes & Feedback</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {journey.driverNotes && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-500">Driver Notes</p>
                    <p className="text-sm text-gray-700">{journey.driverNotes}</p>
                  </div>
                )}
                {journey.customerFeedback && (
                  <div>
                    <p className="text-xs text-gray-500">Customer Feedback</p>
                    <p className="text-sm text-gray-700">{journey.customerFeedback}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status + Payment combined */}
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="mb-2">{getStatusBadge(journey.status)}</div>
                {journey.status === 'completed' ? (
                  <div className="mb-2">
                    <span className="text-2xl font-bold text-green-600">{journey.actualDistance || 0} km</span>
                    <p className="text-xs text-gray-500">Total Distance</p>
                  </div>
                ) : (
                  <div className="mb-2">
                    <span className="text-2xl font-bold text-blue-600">{getProgressPercentage()}%</span>
                    <p className="text-xs text-gray-500 mb-2">Progress</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          journey.status === 'in-progress' ? 'bg-blue-600 animate-pulse' : 'bg-blue-600'
                        }`}
                        style={{ width: `${getProgressPercentage()}%` }}
                      />
                    </div>
                    {journey.estimatedDistance > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {journey.actualDistance || 0} / {journey.estimatedDistance} km
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Payment inline */}
              {journey.status === 'completed' && (
                <div className="border-t mt-3 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-700">Payment</span>
                    {(isAdmin || isDriver) && (
                      <button onClick={handleOpenPayment} className="text-xs text-blue-600 hover:underline">
                        Update
                      </button>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    {getPaymentBadge(journey.paymentStatus || 'pending')}
                    <span className="font-bold text-sm">{formatCurrency(journey.totalCost || 0)}</span>
                  </div>
                  {journey.paidAmount > 0 && journey.paymentStatus === 'partial' && (
                    <p className="text-xs text-orange-600 mt-1">
                      Paid: {formatCurrency(journey.paidAmount)} · Due: {formatCurrency((journey.totalCost || 0) - journey.paidAmount)}
                    </p>
                  )}
                  {journey.paymentMethod && (
                    <p className="text-xs text-gray-500 mt-1 capitalize">Method: {journey.paymentMethod.replace('_', ' ')}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vehicle & Odometer */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center">
                <Car className="h-4 w-4 mr-1" />
                Vehicle
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">Vehicle</span>
                  <span className="font-medium text-xs">{formatName(journey.carId?.make) || 'N/A'} {formatName(journey.carId?.model) || ''}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">Reg No.</span>
                  <span className="font-medium text-xs">{journey.carId?.registrationNumber || 'N/A'}</span>
                </div>
                {journey.rentalType && (
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500">Rental</span>
                    <span className="font-medium text-xs capitalize">{journey.rentalType.replace('-', ' ')}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">Odometer</span>
                  <span className="font-medium text-xs">
                    {journey.startOdometer || 0}{journey.endOdometer ? ` → ${journey.endOdometer}` : ''} km
                  </span>
                </div>

                {/* Odometer Photos */}
                {(journey.photos?.odometerStart || journey.photos?.odometerEnd) && (
                  <div className="flex gap-2 pt-1">
                    {journey.photos?.odometerStart && (
                      <div className="flex-1">
                        <p className="text-[10px] text-gray-400 mb-0.5">Start KM Photo</p>
                        <a href={journey.photos.odometerStart} target="_blank" rel="noopener noreferrer">
                          <img
                            src={journey.photos.odometerStart}
                            alt="Start Odometer"
                            className="h-14 w-full object-cover rounded border hover:opacity-80 cursor-pointer"
                          />
                        </a>
                      </div>
                    )}
                    {journey.photos?.odometerEnd && (
                      <div className="flex-1">
                        <p className="text-[10px] text-gray-400 mb-0.5">End KM Photo</p>
                        <a href={journey.photos.odometerEnd} target="_blank" rel="noopener noreferrer">
                          <img
                            src={journey.photos.odometerEnd}
                            alt="End Odometer"
                            className="h-14 w-full object-cover rounded border hover:opacity-80 cursor-pointer"
                          />
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* Rates - compact row */}
                <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                  {journey.carId?.pricePerKm > 0 && (
                    <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium">
                      {formatCurrency(journey.carId.pricePerKm)}/km
                    </span>
                  )}
                  {journey.carId?.pricePerDay > 0 && (
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">
                      {formatCurrency(journey.carId.pricePerDay)}/day
                    </span>
                  )}
                  {journey.carId?.pricePerHour > 0 && (
                    <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded font-medium">
                      {formatCurrency(journey.carId.pricePerHour)}/hr
                    </span>
                  )}
                  {journey.carId?.outstationPricePerKm > 0 && (
                    <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded font-medium">
                      {formatCurrency(journey.carId.outstationPricePerKm)}/km (out)
                    </span>
                  )}
                  {journey.carId?.driverBataPerDay > 0 && (
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-medium">
                      Bata: {formatCurrency(journey.carId.driverBataPerDay)}/day
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Driver Details - hide if logged-in user is the driver */}
          {(() => {
            const driver = journey.driverId || journey.bookingId?.driverId;
            const driverName = driver?.name || journey.driverName;
            const driverPhone = driver?.phone;
            if (!driver && !driverName) return null;
            const driverId = driver?._id || driver;
            if (user?.role === 'driver' || (driverId && driverId.toString() === user?._id?.toString())) return null;
            return (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Driver</p>
                        <p className="text-sm font-medium">{formatName(driverName) || 'N/A'}</p>
                      </div>
                    </div>
                    {driverPhone && (
                      <a href={`tel:${driverPhone}`} className="p-2 bg-green-500 text-white rounded-full">
                        <Phone className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Customer Details */}
          {journey.customerId && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                      <User className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Customer</p>
                      <p className="text-sm font-medium">{formatName(journey.customerId?.name) || 'N/A'}</p>
                      {journey.customerId?.phone && (
                        <p className="text-xs text-gray-500">{journey.customerId.phone}</p>
                      )}
                    </div>
                  </div>
                  {journey.customerId?.phone && (
                    <a href={`tel:${journey.customerId.phone}`} className="p-2 bg-green-500 text-white rounded-full">
                      <Phone className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rating */}
          {journey.customerRating && (
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">Rating</p>
                <div className="flex gap-3 text-xs">
                  {journey.customerRating.driver && (
                    <span>Driver: <strong>{journey.customerRating.driver}/5</strong></span>
                  )}
                  {journey.customerRating.car && (
                    <span>Car: <strong>{journey.customerRating.car}/5</strong></span>
                  )}
                  {journey.customerRating.overall && (
                    <span className="text-blue-600 font-bold">Overall: {journey.customerRating.overall}/5</span>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Invoice Modal */}
      {showInvoice && (
        <JourneyInvoice
          journey={journey}
          travelsInfo={travelsInfo}
          onClose={() => setShowInvoice(false)}
        />
      )}

      {/* Payment Status Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Update Payment Status"
        size="md"
      >
        <div className="space-y-4">
          {/* Total Amount Display */}
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600">Total Amount</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(journey?.totalCost || 0)}</p>
          </div>

          {/* Payment Status Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'pending', label: 'Pending', color: 'yellow' },
                { value: 'partial', label: 'Partial', color: 'orange' },
                { value: 'paid', label: 'Paid', color: 'green' },
                { value: 'refunded', label: 'Refunded', color: 'red' }
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setPaymentForm(prev => ({
                      ...prev,
                      paymentStatus: opt.value,
                      paidAmount: opt.value === 'paid' ? (journey?.totalCost || 0) : prev.paidAmount
                    }));
                  }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                    paymentForm.paymentStatus === opt.value
                      ? `border-${opt.color}-500 bg-${opt.color}-50 text-${opt.color}-700`
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                  style={paymentForm.paymentStatus === opt.value ? {
                    borderColor: opt.color === 'yellow' ? '#EAB308' : opt.color === 'orange' ? '#F97316' : opt.color === 'green' ? '#22C55E' : '#EF4444',
                    backgroundColor: opt.color === 'yellow' ? '#FEFCE8' : opt.color === 'orange' ? '#FFF7ED' : opt.color === 'green' ? '#F0FDF4' : '#FEF2F2'
                  } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Paid Amount - Show for partial */}
          {paymentForm.paymentStatus === 'partial' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid</label>
              <Input
                type="number"
                value={paymentForm.paidAmount}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, paidAmount: e.target.value }))}
                placeholder="Enter amount paid"
              />
              {paymentForm.paidAmount && (
                <p className="text-xs text-orange-600 mt-1">
                  Remaining: {formatCurrency((journey?.totalCost || 0) - (parseFloat(paymentForm.paidAmount) || 0))}
                </p>
              )}
            </div>
          )}

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <select
              value={paymentForm.paymentMethod}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">Select method</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Payment Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note (Optional)</label>
            <textarea
              value={paymentForm.paymentNote}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentNote: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="e.g., Paid via Google Pay"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowPaymentModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleSavePayment}
              disabled={paymentSaving}
            >
              {paymentSaving ? 'Saving...' : 'Update Payment'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Details Modal (Distance, Duration, Fuel, Odometer) */}
      <Modal
        isOpen={showEditDetails}
        onClose={() => setShowEditDetails(false)}
        title="Edit Trip Details"
        size="md"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Distance (km)</label>
              <Input
                type="number"
                value={editingDetails.actualDistance}
                onChange={(e) => setEditingDetails(prev => ({ ...prev, actualDistance: e.target.value }))}
                placeholder="0"
                className="text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Duration (hrs)</label>
              <Input
                type="number"
                step="0.1"
                value={editingDetails.actualDuration}
                onChange={(e) => setEditingDetails(prev => ({ ...prev, actualDuration: e.target.value }))}
                placeholder="0"
                className="text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Fuel Used (L)</label>
              <Input
                type="number"
                step="0.1"
                value={editingDetails.fuelConsumed}
                onChange={(e) => setEditingDetails(prev => ({ ...prev, fuelConsumed: e.target.value }))}
                placeholder="0"
                className="text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Start Odometer</label>
              <Input
                type="number"
                value={editingDetails.startOdometer}
                onChange={(e) => {
                  const startVal = e.target.value;
                  setEditingDetails(prev => {
                    const endVal = parseFloat(prev.endOdometer) || 0;
                    const startNum = parseFloat(startVal) || 0;
                    const dist = endVal > startNum ? endVal - startNum : prev.actualDistance;
                    return { ...prev, startOdometer: startVal, actualDistance: dist };
                  });
                }}
                placeholder="0"
                className="text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">End Odometer</label>
              <Input
                type="number"
                value={editingDetails.endOdometer}
                onChange={(e) => {
                  const endVal = e.target.value;
                  setEditingDetails(prev => {
                    const startVal = parseFloat(prev.startOdometer) || 0;
                    const endNum = parseFloat(endVal) || 0;
                    const dist = endNum > startVal ? endNum - startVal : prev.actualDistance;
                    return { ...prev, endOdometer: endVal, actualDistance: dist };
                  });
                }}
                placeholder="0"
                className="text-sm"
              />
            </div>
          </div>

          {/* Auto-calculated distance from odometer */}
          {editingDetails.startOdometer && editingDetails.endOdometer && (
            <div className="bg-blue-50 p-2 rounded text-xs text-blue-700">
              Distance from odometer: <strong>{(parseFloat(editingDetails.endOdometer) - parseFloat(editingDetails.startOdometer)) || 0} km</strong> (auto-synced)
            </div>
          )}

          {/* Recalculated total preview */}
          {journey?.carId?.pricePerKm > 0 && editingDetails.actualDistance && (
            <div className="bg-gray-100 p-2 rounded text-xs text-gray-700">
              New base fare: {editingDetails.actualDistance} km × {formatCurrency(journey.carId.pricePerKm)} = <strong>{formatCurrency(parseFloat(editingDetails.actualDistance) * journey.carId.pricePerKm)}</strong>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowEditDetails(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleSaveDetails}
              disabled={detailsSaving}
            >
              {detailsSaving ? 'Saving...' : 'Save Details'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Costs Modal */}
      <Modal
        isOpen={showEditCosts}
        onClose={() => setShowEditCosts(false)}
        title="Edit Cost Details"
        size="md"
      >
        <div className="space-y-4">
          {/* Base Fare - Editable */}
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-blue-800">Base Fare</span>
              <button
                type="button"
                className="text-xs text-blue-600 hover:text-blue-800 underline"
                onClick={() => {
                  const calculated = (journey?.actualDistance || 0) * (journey?.carId?.pricePerKm || 0);
                  setEditingCosts(prev => ({ ...prev, baseCost: calculated }));
                }}
              >
                Recalculate ({journey?.actualDistance || 0} km × {formatCurrency(journey?.carId?.pricePerKm || 0)} = {formatCurrency((journey?.actualDistance || 0) * (journey?.carId?.pricePerKm || 0))})
              </button>
            </div>
            <Input
              type="number"
              value={editingCosts.baseCost}
              onChange={(e) => setEditingCosts(prev => ({ ...prev, baseCost: e.target.value }))}
              className="text-sm"
              placeholder="Base fare amount"
            />
          </div>

          {/* Additional Charges */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-gray-700">Additional Charges</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Toll Charges</label>
                <Input
                  type="number"
                  value={editingCosts.tollCharges}
                  onChange={(e) => setEditingCosts(prev => ({ ...prev, tollCharges: e.target.value }))}
                  placeholder="0"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Parking Charges</label>
                <Input
                  type="number"
                  value={editingCosts.parkingCharges}
                  onChange={(e) => setEditingCosts(prev => ({ ...prev, parkingCharges: e.target.value }))}
                  placeholder="0"
                  className="text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Other Charges</label>
                <Input
                  type="number"
                  value={editingCosts.otherCharges}
                  onChange={(e) => setEditingCosts(prev => ({ ...prev, otherCharges: e.target.value }))}
                  placeholder="0"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Other Charges Note</label>
                <Input
                  type="text"
                  value={editingCosts.otherChargesNote}
                  onChange={(e) => setEditingCosts(prev => ({ ...prev, otherChargesNote: e.target.value }))}
                  placeholder="e.g., Night charges"
                  className="text-sm"
                />
              </div>
            </div>
          </div>

          {/* Discount */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-gray-700">Discount</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Discount Amount</label>
                <Input
                  type="number"
                  value={editingCosts.discount}
                  onChange={(e) => setEditingCosts(prev => ({ ...prev, discount: e.target.value }))}
                  placeholder="0"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Discount Reason</label>
                <Input
                  type="text"
                  value={editingCosts.discountNote}
                  onChange={(e) => setEditingCosts(prev => ({ ...prev, discountNote: e.target.value }))}
                  placeholder="e.g., Regular customer"
                  className="text-sm"
                />
              </div>
            </div>
          </div>

          {/* Preview Total */}
          <div className="bg-gray-900 text-white rounded-lg p-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-300">Base Fare</span>
                <span>{formatCurrency(parseFloat(editingCosts.baseCost) || 0)}</span>
              </div>
              {parseFloat(editingCosts.tollCharges) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-300">Toll</span>
                  <span>+ {formatCurrency(parseFloat(editingCosts.tollCharges))}</span>
                </div>
              )}
              {parseFloat(editingCosts.parkingCharges) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-300">Parking</span>
                  <span>+ {formatCurrency(parseFloat(editingCosts.parkingCharges))}</span>
                </div>
              )}
              {parseFloat(editingCosts.otherCharges) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-300">Other</span>
                  <span>+ {formatCurrency(parseFloat(editingCosts.otherCharges))}</span>
                </div>
              )}
              {parseFloat(editingCosts.discount) > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>Discount</span>
                  <span>- {formatCurrency(parseFloat(editingCosts.discount))}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-700 font-bold text-lg">
                <span>Total</span>
                <span className="text-green-400">
                  {formatCurrency(
                    (parseFloat(editingCosts.baseCost) || 0) +
                    (parseFloat(editingCosts.tollCharges) || 0) +
                    (parseFloat(editingCosts.parkingCharges) || 0) +
                    (parseFloat(editingCosts.otherCharges) || 0) -
                    (parseFloat(editingCosts.discount) || 0)
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowEditCosts(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleSaveCosts}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>

      <Toast
        isOpen={toast.isOpen}
        onClose={() => setToast(p => ({ ...p, isOpen: false }))}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
};

export default JourneyDetails;
